import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

/**
 * The two chip shapes the naming builder uses: a category sitting in the
 * template (reorderable, removable) and a category still available below
 * (click to append).
 *
 * Deliberately click-driven rather than drag-driven — the whole interaction is
 * "put these categories in order", which arrow buttons express in one tap, work
 * on touch, and are operable by keyboard without a drag-sensor abstraction.
 */

interface TemplateChipProps {
  position: number
  label: string
  isFirst: boolean
  isLast: boolean
  onMoveLeft: () => void
  onMoveRight: () => void
  onRemove: () => void
}

export function TemplateChip({
  position,
  label,
  isFirst,
  isLast,
  onMoveLeft,
  onMoveRight,
  onRemove,
}: TemplateChipProps) {
  return (
    <div className="flex items-center gap-0.5 rounded-md border border-brand/60 bg-brand/10 ps-1 pe-1 py-1">
      <button
        type="button"
        onClick={onMoveLeft}
        disabled={isFirst}
        aria-label={`Move ${label} earlier`}
        className="text-system-gray-400 hover:text-white disabled:opacity-25"
      >
        <ChevronLeftIcon className="h-3.5 w-3.5" />
      </button>
      <span className="text-[10px] text-system-gray-500">{position}</span>
      <span className="px-0.5 text-xs font-medium text-white">{label}</span>
      <button
        type="button"
        onClick={onMoveRight}
        disabled={isLast}
        aria-label={`Move ${label} later`}
        className="text-system-gray-400 hover:text-white disabled:opacity-25"
      >
        <ChevronRightIcon className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} from the template`}
        className="ms-0.5 text-system-gray-400 hover:text-system-red-400"
      >
        <XMarkIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

interface PoolChipProps {
  label: string
  /** How many tag values the category has — 0 means nothing to pick yet. */
  optionCount: number
  onAdd: () => void
}

export function PoolChip({ label, optionCount, onAdd }: PoolChipProps) {
  return (
    <button
      type="button"
      onClick={onAdd}
      title={`Add ${label} to the template`}
      className={cn(
        "flex items-center gap-1 rounded-md border border-system-gray-700 bg-system-gray-800/60 px-2 py-1 text-xs text-system-gray-200",
        "hover:border-brand hover:text-white",
      )}
    >
      <PlusIcon className="h-3 w-3" />
      {label}
      <span className="text-[10px] text-system-gray-500">
        {optionCount === 0 ? "no values yet" : optionCount}
      </span>
    </button>
  )
}
